import { BACKGROUND_TASKS_QUERY } from "@lib/graphql/queries";
import { type BackgroundTask, useBackgroundTasksStore } from "@lib/stores/backgroundTasksStore";
import { useEffect } from "react";
import { useQuery } from "urql";

// Backup poll for the BackgroundTask indicator. ActionCable is still the
// primary delivery path; this catches the case where a websocket frame is
// dropped and leaves a task locally stuck as "running".
const POLL_INTERVAL_MS = 15_000;

export function useBackgroundTasksPolling() {
  const [{ data }, executeQuery] = useQuery({
    query: BACKGROUND_TASKS_QUERY,
    requestPolicy: "cache-and-network",
  });
  const reconcile = useBackgroundTasksStore((s) => s.reconcile);

  useEffect(() => {
    if (data?.backgroundTasks) {
      reconcile(data.backgroundTasks as BackgroundTask[]);
    }
  }, [data, reconcile]);

  useEffect(() => {
    const refetch = () => executeQuery({ requestPolicy: "network-only" });

    const intervalId = window.setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      refetch();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [executeQuery]);
}
